/**
 * Prestation graphique — seconde ligne de panier.
 *
 * Le cahier demande que la prestation soit « un produit distinct à trois
 * variantes, ajouté au panier en même temps que le produit principal et relié
 * à sa ligne par une propriété ».
 *
 * Deux garde-fous gouvernent ce fichier :
 *
 * 1. On n'intercepte PAS le formulaire d'Horizon. Il gère l'ajout, ses erreurs,
 *    le rendu du panier et ses animations ; s'y substituer coûterait tout cela.
 *    On attend qu'il ait réussi — l'événement `cartLinesUpdate` — puis on
 *    ajoute la seconde ligne.
 *
 * 2. Un seul ajout par soumission. Notre propre ajout provoque un second
 *    `cartLinesUpdate` ; sans le drapeau, il se rappellerait indéfiniment.
 *
 * Si cet ajout échoue, la commande part quand même : le niveau choisi est déjà
 * inscrit en propriété sur la ligne principale, donc rien n'est perdu — la
 * facturation se rattrape à la main plutôt que de bloquer une vente.
 */

import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class HeyPrestation extends Component {
  #attendAjout = false;
  #formulaire = null;

  connectedCallback() {
    super.connectedCallback();

    this.#formulaire = document.getElementById(this.dataset.formId);
    if (this.#formulaire) this.#formulaire.addEventListener('submit', this.#surEnvoi);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#surPanier);

    // Le palier vit dans le configurateur voisin : on suit ses changements
    // pour dire, ou non, que la création est offerte.
    this.#configurateur?.addEventListener('hey:configuration', this.#surPalier);
    this.#surPalier();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#formulaire?.removeEventListener('submit', this.#surEnvoi);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#surPanier);
    this.#configurateur?.removeEventListener('hey:configuration', this.#surPalier);
  }

  get #configurateur() {
    return this.closest('hey-configurateur');
  }

  /** La variante de prestation retenue, ou null si le niveau est gratuit. */
  get #variante() {
    const coche = this.querySelector('input[type="radio"]:checked');
    const id = coche?.dataset.heyVariante;
    if (!id) return null;

    const prix = Number(coche.closest('[data-hey-prix]')?.dataset.heyPrix || 0);
    return prix > 0 ? { id, prix } : null; // le niveau à 0 CHF n'a pas de ligne
  }

  #surEnvoi = () => {
    this.#attendAjout = true;
  };

  #surPanier = async () => {
    if (!this.#attendAjout) return;
    // Remis à false AVANT l'ajout : notre propre requête provoque un second
    // événement, qui doit trouver le drapeau déjà baissé.
    this.#attendAjout = false;

    const variante = this.#variante;
    if (!variante) return;

    const ligne = this.#formulaire?.querySelector('input[name="id"]')?.value;

    try {
      await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variante.id),
              quantity: 1,
              properties: {
                // Le souligné initial masque la propriété dans le panier et
                // sur la commande : c'est un lien technique, pas une
                // information pour le client.
                _ligne_principale: ligne || '',
              },
            },
          ],
        }),
      });
    } catch (e) {
      // Silencieux à dessein : la commande principale est déjà passée, et le
      // niveau choisi figure en propriété sur sa ligne. Bloquer ou alerter
      // ici ferait perdre une vente pour un problème rattrapable.
      console.error('[heycup] prestation graphique non ajoutée au panier', e);
    }
  };

  /** « offerte à partir de 5 000 » devient un fait quand le palier y est. */
  #surPalier = () => {
    const cible = this.querySelector('[data-hey-champ="offert"]');
    if (!cible) return;

    const seuil = Number(this.dataset.palierGratuit || 0);
    if (!seuil) return;

    // Le nombre de pieces vient de l'evenement du configurateur, jamais
    // d'une lecture du libelle d'option.
    const pieces = Number(this.#configurateur?.dataset.heyPieces || 0);

    cible.classList.toggle('hey-presta__offert--acquis', pieces >= seuil);
  };
}

if (!customElements.get('hey-prestation')) {
  customElements.define('hey-prestation', HeyPrestation);
}
